import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-200">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该文章不存在或已被删除</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors">
              <i className="ri-home-4-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={`${article.title} - 黑马磁力`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://heimacili.com/article/${article.id}`}
        ogType="article"
      />
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        publisher: { '@type': 'Organization', name: '黑马磁力', url: 'https://heimacili.com' },
      }} />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[
          { label: '首页', to: '/' },
          { label: article.title },
        ]} />

        <article className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded text-xs bg-accent-100 text-accent-700 font-medium">{tag}</span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
              </div>
            </div>

            <div className="aspect-[21/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose max-w-none text-foreground-700 leading-relaxed text-sm"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200">
              <h4 className="font-heading text-lg font-bold text-foreground-900 mb-6">相关文章</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="group bg-background-50 rounded-lg border border-background-200 p-4 hover:border-primary-300 transition-all"
                  >
                    <h5 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {rel.title}
                    </h5>
                    <p className="text-xs text-foreground-500 line-clamp-2">{rel.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}